/**
 * CandidatePageV2 — Candidate detail v2 (V1-treatment relayout).
 *
 * Adopts the V1 candidate-page layout (`pages/app/CandidatePage.tsx`): a COMPACT
 * header/action strip — candidate name + tenant-status pill + a single rank-meta
 * line ("Ranked #N of T · X% Match · {Fit} Fit · {Tenure} Tenure") + the action
 * cluster (Advance stage · Schedule interview · Download Write-Up · kebab ⋯) —
 * over the shadcn Tabs bar (Resume · AI Evaluation · Interview Prep · Interview
 * Analysis · Notes), with the left meta rail owning the ONE candidate photo.
 *
 * The prior tall photo-hero (a second candidate photo, a big "AI FIT SCORE" box,
 * and an AI-why blurb that all duplicated the left rail's photo / rank ring / AI
 * Ranking Summary) is removed. Exactly one photo renders (in the rail); the fit %
 * folds into the header rank-meta line; the AI summary lives only in the rail.
 *
 * Feature parity with v1 (`pages/app/CandidatePage.tsx`) is fully reclaimed —
 * nothing the v1 page did is dropped:
 *   • Every tab body is the EXACT v1 component (`ResumeDocument`,
 *     `AiEvaluationTab`, `InterviewPrepTab`, `InterviewAnalysisTab`, `NotesTab`),
 *     wired with the same props + the same data hooks. No tab logic is stubbed.
 *   • All header ACTIONS are back: pipeline-stage advance + schedule-interview
 *     (real `useUpdateCandidateStatus` PUT), Download Write-Up PDF
 *     (`ExportClientWriteupButton`), and the kebab overflow menu
 *     (`CandidateKebabMenu`) — also-consider-for-another-role, change tenant
 *     status, re-run AI evaluation, copy candidate ID, delete — with the full
 *     modal set (`CrossJdModal`, `TenantStatusModal`, `CascadePromptModal`,
 *     `RegenerateConfirmModal`, `DeleteCandidateModal`) and cascade orchestration.
 *   • The v1 meta rail is reclaimed verbatim via `CandidatePageSidebar`
 *     (contact & links, resume download, LinkedIn, rank ring + fit chips, key
 *     skills, AI ranking summary, rank_one AI rating widget, notes quick-add +
 *     recent, Outcomes capture).
 *   • Multi-JD selector (2+ pairings, no ?jd=) and the zero-pairings orphan
 *     banner ("Add to a job →") are carried over.
 *
 * Data hooks are shared with v1 — NO backend is duplicated: `useCandidatePolling`,
 * `useRanking`, `useJds`, `useCandidatePairings`, `useRankingRunByResource`,
 * `useUpdateCandidateStatus`, `useTenantStatusMutation`, `useDeleteCandidate`,
 * `useStartInterviewAnalysis`. The `?tab=` param uses the same `resolveActiveTab`
 * validator for deep-link parity.
 *
 * ── Token discipline ─────────────────────────────────────────────────────────
 * No raw hex literals. Surface/text/border semantics use the direct v2 token
 * utilities (text-text-primary, text-text-secondary, bg-surface-card, …): under
 * [data-theme="v2"] the flat shadcn vars (--foreground/--muted-foreground/--card)
 * re-point to HEX, so wrapping them in hsl() is INVALID CSS (renders black/
 * invisible) — never do that. Only hsl(var(--strong/--moderate/--weak/--brand))
 * is safe here, because those are genuine HSL triples (the fit-band / brand set).
 * Blue is the workhorse marker; teal is the one hero ring.
 */
import { useEffect, useMemo, useState, type JSX } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCandidatePolling } from "@/hooks/queries/candidates/useCandidatePolling";
import { useRanking } from "@/hooks/queries/candidates/useRanking";
import { useRankingRunByResource } from "@/hooks/queries/candidates/useRankingRunByResource";
import { useJds } from "@/hooks/queries/candidates/useJds";
import { useCandidatePairings } from "@/hooks/useCandidatePairings";
import { useUpdateCandidateStatus } from "@/hooks/queries/candidates/useUpdateCandidateStatus";
import { useTenantStatusMutation } from "@/hooks/useTenantStatusMutation";
import { useDeleteCandidate } from "@/hooks/queries/candidates/useDeleteCandidate";
import { useStartInterviewAnalysis } from "@/hooks/queries/interview-analysis/useStartInterviewAnalysis";
import { useToastStore } from "@/stores/useToastStore";

import { ApiError } from "@/lib/apiError";
import {
  postJdPairing,
  type TenantStatus,
  type TerminalTenantStatus,
  type JdPairingRow,
} from "@/lib/candidatesApi";
import { fitScoreFromOverall } from "@/lib/fitScore";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import type { Jd } from "@/hooks/queries/candidates/useJd";

import { Button } from "@/components/ui/button";
import { DetailBackLink } from "@/components/v2/patterns/DetailPageV2";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusPill } from "@/components/candidates/StatusPill";
import { cn } from "@/lib/utils";

// v1 candidate-page pieces — reused verbatim (no re-implementation).
import { CandidatePageSidebar } from "@/components/candidate-page/CandidatePageSidebar";
import { CandidateKebabMenu } from "@/components/candidate-page/CandidateKebabMenu";
import { ExportClientWriteupButton } from "@/components/candidate-page/ExportClientWriteupButton";
import { CrossJdModal } from "@/components/candidate-page/CrossJdModal";
import { TenantStatusModal } from "@/components/candidate-page/TenantStatusModal";
import { CascadePromptModal } from "@/components/candidate-page/CascadePromptModal";
import { RegenerateConfirmModal } from "@/components/candidate-page/RegenerateConfirmModal";
import { DeleteCandidateModal } from "@/components/candidate-page/DeleteCandidateModal";

// v1 candidate-page tab content — mounted verbatim inside the v2 shell.
import { ResumeDocument } from "@/components/candidate-page/ResumeDocument";
import { AiEvaluationTab } from "@/components/candidate-page/tabs/AiEvaluationTab";
import { InterviewPrepTab } from "@/components/candidate-page/tabs/InterviewPrepTab";
import { InterviewAnalysisTab } from "@/components/candidate-page/tabs/InterviewAnalysisTab";
import { NotesTab } from "@/components/candidate-page/tabs/NotesTab";
import { resolveActiveTab, type TabKey } from "@/components/candidate-page/tabs";

// ── Pipeline order for the Advance-stage action (non-terminal progression) ──
const PIPELINE_ORDER: readonly CandidateStatus[] = [
  "active",
  "phone_screen",
  "interview_scheduled",
  "interviewed",
  "offer",
  "hired",
];

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  phone_screen: "Phone Screen",
  interview_scheduled: "Interview Scheduled",
  interviewed: "Interviewed",
  offer: "Offer Extended",
  hired: "Hired",
  rejected: "Rejected",
  opted_out: "Opted Out",
};

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "hired",
  "rejected",
  "opted_out",
]);
const TERMINAL_TENANT_STATUSES: ReadonlySet<string> = new Set([
  "hired",
  "rejected",
  "opted_out",
]);

// Which JD pairing a contextless deep-link (search result, All-Candidates row,
// shared/bookmarked URL) should adopt when it lands here with no `?jd=`. A
// candidate is only meaningful in a JD context — without one the page renders
// the degraded shell (no rank ring, AI summary, or per-JD status). Pick the
// pairing the recruiter is most likely working right now: an in-flight
// (non-terminal) pairing over a closed one, and within each group the most
// recently touched. The header selector still lets them switch afterward.
function pickDefaultPairing(
  pairings: readonly JdPairingRow[],
): JdPairingRow | undefined {
  if (pairings.length === 0) return undefined;
  return [...pairings].sort((a, b) => {
    const aTerminal = a.terminal_outcome !== null;
    const bTerminal = b.terminal_outcome !== null;
    if (aTerminal !== bTerminal) return aTerminal ? 1 : -1; // non-terminal first
    return b.updated_at.localeCompare(a.updated_at); // most recently touched
  })[0];
}

function tenantStatusLabel(s: TenantStatus): string {
  if (s === "opted_out") return "Opted Out";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Kebab modal orchestration state (mirrors v1 CandidatePageHeader) ──────────
type ModalState =
  | { kind: "none" }
  | { kind: "cross-jd" }
  | { kind: "status" }
  | { kind: "cascade"; newStatus: TerminalTenantStatus; otherInFlightCount: number }
  | { kind: "rerun-eval" }
  | { kind: "delete" };

export default function CandidatePageV2(): JSX.Element {
  const { tenantSlug, candidateId } = useParams<{
    tenantSlug: string;
    candidateId: string;
  }>();
  const base = useTenantBase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();

  const jdId = searchParams.get("jd") ?? undefined;
  // Same URL-param validator v1 uses (deep-link parity + type-safety).
  const activeTab: TabKey = resolveActiveTab(searchParams.get("tab"));

  const onTabChange = (val: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", val);
    // Push so the back button cycles tabs — matches v1 §2.7.
    setSearchParams(next, { replace: false });
  };
  const onTabChangeTyped = (key: TabKey): void => onTabChange(key);

  // ── Data hooks (shared with v1 — no backend duplication) ────────────────────
  const candidateQ = useCandidatePolling(candidateId);
  const rankingQ = useRanking({ jdId });
  const jdsQ = useJds();
  const allJdsQ = useJds({ statusFilter: "all" });
  const pairingsQ = useCandidatePairings(candidateId);
  const rankingRunQ = useRankingRunByResource({ jdId });

  // ── Mutations (shared hooks) ────────────────────────────────────────────────
  const updateStatus = useUpdateCandidateStatus();
  const tenantStatusMutation = useTenantStatusMutation();
  const deleteCandidate = useDeleteCandidate();
  const startInterviewAnalysis = useStartInterviewAnalysis();

  const [modal, setModal] = useState<ModalState>({ kind: "none" });

  // Derived: ranking row for this candidate.
  const rankingRow = useMemo(() => {
    if (!candidateQ.data) return null;
    if (!rankingQ.data) return null;
    return (
      rankingQ.data.find((r) => r.candidate_id === candidateQ.data!.id) ?? null
    );
  }, [candidateQ.data, rankingQ.data]);

  const rankCohortTotal = rankingQ.data?.length ?? null;

  // JD list + context.
  const jds = useMemo(() => jdsQ.data ?? [], [jdsQ.data]);
  const jdName = useMemo(() => {
    if (!jdId) return null;
    return jds.find((j) => j.id === jdId)?.name ?? null;
  }, [jdId, jds]);
  const jdSummaries = useMemo(
    () => jds.map((j) => ({ id: j.id, name: j.name })),
    [jds],
  );

  // Multi-JD switcher options: any candidate with 2+ pairings. Computed
  // regardless of `jdId` — a contextless deep-link now auto-resolves a default
  // JD (see the effect below), so the switcher must persist AFTER a JD is set
  // to remain the way to reach the candidate's other pairings.
  const multiJdOptions = useMemo(() => {
    const pairings = pairingsQ.data;
    if (!pairings || pairings.length < 2) return undefined;
    return pairings
      .map((p) => {
        const jd = jds.find((j) => j.id === p.jd_id);
        return jd ? { id: jd.id, title: jd.name } : null;
      })
      .filter((x): x is { id: string; title: string } => x !== null);
  }, [pairingsQ.data, jds]);

  // Contextless deep-link auto-resolve: land here with no `?jd=` and the
  // candidate has ≥1 pairing → adopt a sensible default JD so the full ranked
  // view resolves instead of the degraded shell. Covers search results,
  // All-Candidates rows, and shared/bookmarked URLs alike (none carry a JD).
  // `replace` keeps the back button pointing at the referrer, not this URL
  // sans `?jd`. Zero-pairing orphans fall through to the orphan banner.
  useEffect(() => {
    if (jdId) return;
    const pairings = pairingsQ.data;
    if (!pairings || pairings.length === 0) return;
    const target = pickDefaultPairing(pairings);
    if (!target) return;
    const next = new URLSearchParams(searchParams);
    next.set("jd", target.jd_id);
    setSearchParams(next, { replace: true });
  }, [jdId, pairingsQ.data, searchParams, setSearchParams]);

  // Zero-pairings orphan candidate (v1 spec §2.3 case 3).
  const isOrphanCandidate =
    pairingsQ.isSuccess && (pairingsQ.data?.length ?? 0) === 0;

  // ── Cross-JD add mutation (no-eval slot; identical to v1 header) ─────────────
  const crossJdMutation = useMutation<void, ApiError, { jdId: string; jdTitle: string }>({
    mutationFn: async (vars) => {
      await postJdPairing(tenantSlug!, candidateId!, vars.jdId);
    },
    onSuccess: (_data, vars) => {
      const first = candidateQ.data?.name.split(" ")[0] ?? "Candidate";
      addToast(`${first} added to ${vars.jdTitle}.`, "success");
      void queryClient.invalidateQueries({
        queryKey: ["candidate-pairings", tenantSlug, candidateId],
      });
      setModal({ kind: "none" });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        addToast("Already in that pipeline.", "warning");
      } else {
        addToast("Couldn't add to that job. Try again.", "error");
      }
      setModal({ kind: "none" });
    },
  });

  // ── Back navigation (preserve ?jd= context) ─────────────────────────────────
  const backHref = `${base}/candidates${jdId ? `?jd=${jdId}` : ""}`;

  // ── 404 ─────────────────────────────────────────────────────────────────────
  if (
    candidateQ.isError &&
    candidateQ.error instanceof ApiError &&
    candidateQ.error.status === 404
  ) {
    return (
      <div
        data-testid="page-ready"
        className="flex flex-col items-center justify-center"
        style={{ minHeight: "240px" }}
      >
        <div
          className="text-text-primary"
          style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}
        >
          Candidate not found
        </div>
        <div
          className="text-text-secondary"
          style={{ fontSize: "13.5px", marginBottom: "16px" }}
        >
          This candidate may have been deleted or you may not have access.
        </div>
        <DetailBackLink label="Back to All Candidates" href={backHref} />
      </div>
    );
  }

  // ── Other error ─────────────────────────────────────────────────────────────
  if (candidateQ.isError) {
    return (
      <div
        data-testid="page-ready"
        className="flex flex-col items-center justify-center"
        style={{ minHeight: "240px" }}
      >
        <div
          className="text-text-primary"
          style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}
        >
          Couldn&apos;t load this candidate
        </div>
        <div
          className="text-text-secondary"
          style={{ fontSize: "13.5px", marginBottom: "16px" }}
        >
          Refresh in a moment.
        </div>
        <Button variant="outline" size="sm" onClick={() => candidateQ.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (candidateQ.isLoading || !candidateQ.data) {
    return (
      <div
        data-testid="candidate-loading"
        className="flex items-center justify-center text-text-tertiary text-sm"
        style={{ minHeight: "240px" }}
      >
        Loading…
      </div>
    );
  }

  const candidate = candidateQ.data;
  const firstName = candidate.name.split(" ")[0] ?? candidate.name;
  const score = rankingRow?.fit_score ?? null;
  const displayScore =
    score ?? (rankingRow ? fitScoreFromOverall(rankingRow.fit_overall) : null);
  const jdSelected = !!jdId;

  // Resolve tenant_status — prefer new column; fall back to legacy mapping.
  const resolvedTenantStatus: TenantStatus =
    ((candidate as { tenant_status?: TenantStatus | null }).tenant_status ??
      (candidate.status === "hired"
        ? "hired"
        : candidate.status === "rejected"
          ? "rejected"
          : candidate.status === "opted_out"
            ? "opted_out"
            : "available")) as TenantStatus;

  const otherInFlightCount = (pairingsQ.data ?? []).filter(
    (p) => p.terminal_outcome === null && p.jd_id !== jdId,
  ).length;
  const pairedJdIds = new Set<string>((pairingsQ.data ?? []).map((p) => p.jd_id));

  // ── Stage controls (real status PUT) ─────────────────────────────────────────
  // `Advance to <next>` is the one-click fast path, but it is forward-only and
  // was for a while the ONLY stage control on this page: a recruiter who
  // advanced a candidate by mistake — or who needs to walk one back after a
  // failed final round — had no way to return them to a prior stage. The stage
  // select is the bidirectional version of the same action over the same
  // domain: any pipeline stage, in either direction.
  //
  // Scope is deliberately PIPELINE_ORDER only. `rejected` / `opted_out` are
  // tenant-level outcomes with cascade semantics across every JD the candidate
  // is paired to, and the kebab's "Change status" modal owns that flow — a bare
  // status PUT from here would skip the cascade prompt. When the candidate IS
  // in one of those states we still surface it as a (disabled) option so the
  // select shows the truth, and picking a pipeline stage brings them back:
  // the server clears `terminal_outcome` for any non-terminal target, so
  // re-entry is a supported transition rather than a repair.
  const currentIdx = PIPELINE_ORDER.indexOf(candidate.status as CandidateStatus);
  const isTerminal = TERMINAL_STATUSES.has(candidate.status);
  const nextStage: CandidateStatus | null =
    !isTerminal && currentIdx >= 0 && currentIdx < PIPELINE_ORDER.length - 1
      ? PIPELINE_ORDER[currentIdx + 1]!
      : null;
  // Set when the candidate sits in a tenant-terminal state that is NOT on the
  // pipeline ladder (rejected / opted_out). `hired` IS on the ladder, so it
  // stays a normal, re-selectable option.
  const offLadderStatus: string | null =
    currentIdx < 0 ? candidate.status : null;

  const patchStatus = (next: CandidateStatus, verb: string): void => {
    updateStatus.mutate(
      { id: candidate.id, status: next },
      {
        onSuccess: () =>
          addToast(`${firstName} ${verb} ${STATUS_LABELS[next] ?? next}.`, "success"),
        onError: () => addToast("Couldn't update status. Try again.", "error"),
      },
    );
  };

  const handleAdvance = (): void => {
    if (!nextStage) return;
    patchStatus(nextStage, "advanced to");
  };
  const handleSchedule = (): void => {
    patchStatus("interview_scheduled", "moved to");
  };
  const handleStageSelect = (next: string): void => {
    if (next === candidate.status) return;
    const target = next as CandidateStatus;
    const targetIdx = PIPELINE_ORDER.indexOf(target);
    // The toast names the direction, because the two mean different things to
    // a recruiter reading it later ("moved back to Interviewed" is a correction).
    const verb =
      currentIdx >= 0 && targetIdx >= 0 && targetIdx < currentIdx
        ? "moved back to"
        : "moved to";
    patchStatus(target, verb);
  };

  // ── Kebab handlers (mirror v1 CandidatePageHeader orchestration) ─────────────
  const handleChangeStatusSave = (newStatus: TenantStatus): void => {
    if (TERMINAL_TENANT_STATUSES.has(newStatus) && otherInFlightCount > 0) {
      setModal({
        kind: "cascade",
        newStatus: newStatus as TerminalTenantStatus,
        otherInFlightCount,
      });
      return;
    }
    tenantStatusMutation.mutate(
      { candidateId: candidate.id, tenantStatus: newStatus, cascade: "keep_active" },
      {
        onSuccess: () => {
          addToast(`${candidate.name} marked as ${tenantStatusLabel(newStatus)}.`, "success");
          setModal({ kind: "none" });
        },
        onError: () => {
          addToast("Couldn't update status. Try again.", "error");
          setModal({ kind: "none" });
        },
      },
    );
  };

  const fireCascade = (mode: "keep_active" | "apply_to_all"): void => {
    if (modal.kind !== "cascade") return;
    const { newStatus } = modal;
    tenantStatusMutation.mutate(
      { candidateId: candidate.id, tenantStatus: newStatus, cascade: mode },
      {
        onSuccess: (data) => {
          const n = data.cascade_summary.jd_pairings_mutated.length;
          const suffix =
            mode === "apply_to_all" && n > 0
              ? ` across ${n} job${n === 1 ? "" : "s"}.`
              : ".";
          addToast(`${candidate.name} marked as ${tenantStatusLabel(newStatus)}${suffix}`, "success");
          setModal({ kind: "none" });
        },
        onError: () => {
          addToast("Couldn't update status. Try again.", "error");
          setModal({ kind: "none" });
        },
      },
    );
  };

  const handleRerunEval = (): void => {
    if (!jdId) {
      addToast("Open this candidate from a job to re-run evaluation.", "warning");
      return;
    }
    setModal({ kind: "rerun-eval" });
  };
  const handleRerunEvalConfirm = (): void => {
    if (!jdId) return;
    startInterviewAnalysis.mutate(
      { candidate_id: candidate.id, type: "pre", jd_id: jdId },
      {
        // Genuine regenerate started — the hook has flipped the analysis to
        // its `generating` phase, but that happens in the rail below the fold,
        // so confirm the action explicitly. The `already_generated` /
        // in-progress / credit cases toast their own feedback from the hook's
        // onError, so this fires only on a real start.
        onSuccess: () =>
          addToast(
            "Re-evaluation started — updated results will appear shortly.",
            "success",
          ),
        onSettled: () => setModal({ kind: "none" }),
      },
    );
  };

  const handleCopyId = (): void => {
    if (!navigator.clipboard) return;
    void navigator.clipboard
      .writeText(candidate.id)
      .then(() => addToast("Candidate ID copied.", "success"));
  };

  const handleDeleteConfirm = (): void => {
    deleteCandidate.mutate(candidate.id, {
      onSuccess: () => {
        setModal({ kind: "none" });
        navigate(backHref);
      },
      onError: () => addToast("Couldn't delete candidate. Try again.", "error"),
    });
  };

  // ── Sidebar candidate shape (v1 parity) ─────────────────────────────────────
  const sidebarCandidate = {
    id: candidate.id,
    name: candidate.name,
    email: candidate.email,
    phone: candidate.phone,
    status: candidate.status,
    filename: candidate.filename,
    linkedin_url: candidate.linkedin_url,
    storage_key_present: candidate.storage_key_present,
    pipelineStage: candidate.status,
  };

  // ── Compact header/action bar (V1 treatment in v2 styling) ──────────────────
  // Mirrors V1 `CandidatePageHeader`'s compact strip — identity + status pill +
  // a single rank-meta line + action cluster — with NO candidate photo (the left
  // meta rail owns the ONE photo), NO big AI-fit-score box (the fit % folds into
  // the rank-meta line), and NO AI-why blurb (the rail's "AI Ranking Summary"
  // owns it). Every action is preserved: Advance stage, Schedule interview,
  // Download Write-Up (PDF), and the kebab overflow (cross-JD, change status,
  // re-run eval, copy ID, delete) with the full modal set. Structure mirrors V1
  // rather than importing its header verbatim, because V1's header hardcodes a
  // dashboard back-nav and has no Advance/Schedule actions.
  const headerBar = (
    <div
      data-testid="candidate-header-bar"
      className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[var(--radius)] border border-border-default bg-surface-card px-[18px] py-[13px] shadow-[var(--shadow-sm)]"
    >
      {/* Identity + meta block (no photo — the rail owns the single photo). */}
      <div className="flex min-w-[240px] flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1
            className="truncate text-text-primary"
            style={{ fontSize: "18px", fontWeight: 650, letterSpacing: "-0.02em", margin: 0 }}
            title={candidate.name}
          >
            {candidate.name}
          </h1>
          <StatusPill variant="tenant" value={resolvedTenantStatus} size="sm" />
        </div>

        {/* Context row — Applied-for JD (single) or multi-JD selector. */}
        {jdName && !multiJdOptions ? (
          <div className="text-[11.5px] text-text-secondary">
            Applied for{" "}
            <span className="font-medium text-text-primary">{jdName}</span>
          </div>
        ) : null}
        {multiJdOptions && multiJdOptions.length > 1 ? (
          <label className="inline-flex items-center gap-1.5 text-[11.5px] text-text-secondary">
            Applied for:
            <select
              data-testid="candidate-jd-selector"
              aria-label="Select job context"
              value={jdId ?? ""}
              onChange={(e) => {
                if (!e.target.value) return;
                const next = new URLSearchParams(searchParams);
                next.set("jd", e.target.value);
                navigate(`?${next.toString()}`, { replace: true });
              }}
              className="rounded border border-border-default bg-surface-card px-2 py-0.5 text-xs font-medium text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-secondary"
            >
              <option value="" disabled>
                Select a job…
              </option>
              {multiJdOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {/* Rank-meta line — folds the fit % in (no separate score box); the rail
            rank ring shows the same %, but the header adds rank/fit/tenure at a
            glance without a second photo or a duplicated AI summary. */}
        {rankingRow && jdSelected ? (
          <div
            className="text-[11.5px] text-text-secondary"
            data-testid="candidate-header-rank-meta"
          >
            <span className="font-medium text-text-primary">
              Ranked #{rankingRow.rank_position}
              {rankCohortTotal != null ? ` of ${rankCohortTotal}` : ""}
            </span>
            {displayScore != null ? (
              <>
                <span className="mx-1.5 text-text-tertiary" aria-hidden>·</span>
                <span>{displayScore}% Match</span>
              </>
            ) : null}
            <span className="mx-1.5 text-text-tertiary" aria-hidden>·</span>
            <span>
              {rankingRow.fit_overall.charAt(0).toUpperCase() +
                rankingRow.fit_overall.slice(1)}{" "}
              Fit
            </span>
            <span className="mx-1.5 text-text-tertiary" aria-hidden>·</span>
            <span>
              {rankingRow.tenure_signal === "concern"
                ? "Concern"
                : rankingRow.tenure_signal.charAt(0).toUpperCase() +
                  rankingRow.tenure_signal.slice(1)}{" "}
              Tenure
            </span>
          </div>
        ) : null}
      </div>

      {/* Action cluster — every action preserved; laid out inline (V1 idiom). */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        {/* Bidirectional stage control — see the `offLadderStatus` block above
            for why the domain is PIPELINE_ORDER and not the full 8-value enum. */}
        <label className="inline-flex items-center gap-1.5 text-[11.5px] text-text-secondary">
          Stage:
          <select
            data-testid="candidate-stage-select"
            aria-label="Pipeline stage"
            value={candidate.status}
            disabled={updateStatus.isPending}
            onChange={(e) => handleStageSelect(e.target.value)}
            className="rounded border border-border-default bg-surface-card px-2 py-1 text-xs font-medium text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-secondary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {offLadderStatus ? (
              <option value={offLadderStatus} disabled>
                {STATUS_LABELS[offLadderStatus] ?? offLadderStatus}
              </option>
            ) : null}
            {PIPELINE_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s] ?? s}
              </option>
            ))}
          </select>
        </label>
        <Button
          size="sm"
          onClick={handleAdvance}
          disabled={!nextStage || updateStatus.isPending}
          data-testid="candidate-advance-stage"
        >
          {nextStage
            ? `Advance to ${STATUS_LABELS[nextStage] ?? nextStage}`
            : "Advance stage"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSchedule}
          disabled={
            isTerminal ||
            candidate.status === "interview_scheduled" ||
            updateStatus.isPending
          }
          data-testid="candidate-schedule-interview"
        >
          Schedule interview
        </Button>
        <ExportClientWriteupButton
          candidateId={candidate.id}
          candidateName={candidate.name}
          role={jdName}
        />
        <CandidateKebabMenu
          onCrossJd={() => setModal({ kind: "cross-jd" })}
          onChangeStatus={() => setModal({ kind: "status" })}
          onRerunEval={handleRerunEval}
          onCopyId={handleCopyId}
          onDelete={() => setModal({ kind: "delete" })}
        />
      </div>
    </div>
  );

  // ── Tab content — REAL v1 components mounted verbatim ────────────────────────
  const tabs: { value: TabKey; label: string; content: JSX.Element }[] = [
    {
      value: "resume",
      label: "Resume",
      content: <ResumeDocument candidate={candidate} />,
    },
    {
      value: "eval",
      label: "AI Evaluation",
      content: (
        <AiEvaluationTab candidate={candidate} jdId={jdId} jds={jdSummaries} />
      ),
    },
    {
      value: "prep",
      label: "Interview Prep",
      content: (
        <InterviewPrepTab
          candidate={candidate}
          jdId={jdId}
          jds={jdSummaries}
          activeTab={activeTab}
        />
      ),
    },
    {
      value: "analysis",
      label: "Interview Analysis",
      content: (
        <InterviewAnalysisTab
          candidate={candidate}
          jdId={jdId}
          jds={jdSummaries}
          activeTab={activeTab}
          onTabChange={onTabChangeTyped}
        />
      ),
    },
    {
      value: "notes",
      label: "Notes",
      content: (
        <NotesTab
          candidate={candidate}
          activeTab={activeTab}
          jdId={jdId}
          jdTitle={jdName ?? undefined}
        />
      ),
    },
  ];

  return (
    <div data-testid="page-ready" className="flex flex-col gap-5">
      <DetailBackLink
        label="Back to All Candidates"
        onClick={() => navigate(backHref)}
      />

      {headerBar}

      {/* Zero-pairings orphan banner (v1 spec §2.3 case 3). */}
      {isOrphanCandidate && (
        <div
          data-testid="candidate-orphan-banner"
          className="flex items-center gap-3 rounded-[var(--radius)] border border-status-pending-ring bg-status-pending-bg px-4 py-3 text-sm"
        >
          <span className="flex-1 text-text-primary">
            This candidate is not associated with any job.
          </span>
          <button
            type="button"
            onClick={() => setModal({ kind: "cross-jd" })}
            className="font-medium text-text-link hover:underline"
            data-testid="candidate-orphan-add-job"
          >
            Add to a job →
          </button>
        </div>
      )}

      {/* Body — LEFT meta rail (candidate detail, read first per founder
          directive: "people read left to right") + tabs to its right. */}
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        {/* Reclaimed v1 meta rail: contact & links, resume DL, LinkedIn, rank
            ring + fit chips, key skills, AI ranking summary, rank_one rating
            widget, notes quick-add + recent, Outcomes capture. Sits on the
            LEFT so the candidate's dossier is read before the tab content. */}
        <aside className="lg:w-[300px] lg:shrink-0" data-testid="candidate-meta-rail">
          <CandidatePageSidebar
            candidate={sidebarCandidate}
            ranking={rankingRow}
            rankTotal={rankCohortTotal}
            jdSelected={jdSelected}
            onAllNotesClick={() => onTabChange("notes")}
            jdId={jdId}
            rankOneRunId={rankingRunQ.data?.run_id ?? null}
            rankOneModelUsed={rankingRunQ.data?.model_used ?? null}
          />
        </aside>

        <div className="min-w-0 flex-1">
          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((t) => (
              <TabsContent key={t.value} value={t.value}>
                <div className={cn("flex flex-col gap-4")}>{t.content}</div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* ── Modals (kebab-driven; shared with v1 hooks) ─────────────────────── */}
      {modal.kind === "cross-jd" ? (
        <CrossJdModal
          candidateName={candidate.name}
          jds={(allJdsQ.data ?? []) as Jd[]}
          pairedJdIds={pairedJdIds}
          isPending={crossJdMutation.isPending}
          onAdd={(id, title) => crossJdMutation.mutate({ jdId: id, jdTitle: title })}
          onCancel={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "status" ? (
        <TenantStatusModal
          currentStatus={resolvedTenantStatus}
          candidateName={candidate.name}
          isPending={tenantStatusMutation.isPending}
          onSave={handleChangeStatusSave}
          onCancel={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "cascade" ? (
        <CascadePromptModal
          candidateName={candidate.name}
          jdTitle={jdName ?? "this job"}
          newStatus={modal.newStatus}
          otherInFlightCount={modal.otherInFlightCount}
          isPending={tenantStatusMutation.isPending}
          onKeepActive={() => fireCascade("keep_active")}
          onApplyToAll={() => fireCascade("apply_to_all")}
          onCancel={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "rerun-eval" ? (
        <RegenerateConfirmModal
          title="Re-run AI evaluation?"
          body="CertAIn will re-evaluate this candidate against the current job and replace the existing ranking. This uses 1 credit and can't be undone."
          confirmLabel="Re-run evaluation"
          isPending={startInterviewAnalysis.isPending}
          onConfirm={handleRerunEvalConfirm}
          onCancel={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "delete" ? (
        <DeleteCandidateModal
          candidateName={candidate.name}
          pairingCount={(pairingsQ.data ?? []).length}
          isPending={deleteCandidate.isPending}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setModal({ kind: "none" })}
        />
      ) : null}
    </div>
  );
}
